import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Container,
} from '@mui/material';
import {
  DisabledByDefaultRounded,
  LocationOn,
  ContentCopy,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import COLORS from '@/resources/style/Themes';
import { StringKeys } from '@/resources/strings/StringKeys';
import { formatTimeAgo } from '@/utils/time/formatTime';
import ConnectionStatus from '@/enums/ConnectionStatus';
import UserStatusChip from '@/components/map/UserStatusChip';
import { DASHBOARD_COLORS } from './styles';
import { useUserContext } from '@/context_providers/user_context/UserProvider';

const UserDashboardHeader = ({
  userInfo,
  userStatus,
  onClose,
  tabValue,
  setTabValue,
}) => {
  const { t } = useTranslation();
  const { isSafeguardDev } = useUserContext();

  const compassStatus = userStatus?.getCalculatedConnectionStatus();

  const lastUpdated = userStatus?.dto?.timestamp
    ? userStatus.dto.timestamp * 1000
    : null;
  const recentLocation = userStatus?.dto?.location;

  const locationToDisplay = recentLocation
    ? {
        lat: recentLocation.latitude,
        lng: recentLocation.longitude,
      }
    : null;

  const handleCoordinatesClick = () => {
    if (locationToDisplay) {
      const { lat, lng } = locationToDisplay;
      window.open(`https://maps.google.com/?q=${lat},${lng}`, '_blank');
    }
  };
  const COPY_FEEDBACK_DURATION = 2000;

  const [isPhoneCopied, setIsPhoneCopied] = useState(false);
  const handlePhoneCopyClick = async () => {
    try {
      await navigator.clipboard.writeText(userInfo?.phoneNumber);
      setIsPhoneCopied(true);
      setTimeout(() => {
        setIsPhoneCopied(false);
      }, COPY_FEEDBACK_DURATION);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const [isLocationCopied, setIsLocationCopied] = useState(false);
  const handleLocationCopyClick = async (e) => {
    e.stopPropagation();
    try {
      if (!locationToDisplay?.lat || !locationToDisplay?.lng) return;

      const lat = Number(locationToDisplay.lat);
      const lng = Number(locationToDisplay.lng);
      const text = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;

      await navigator.clipboard.writeText(text);
      setIsLocationCopied(true);
      setTimeout(() => setIsLocationCopied(false), COPY_FEEDBACK_DURATION);
    } catch (err) {
      console.error('Failed to copy location: ', err);
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: DASHBOARD_COLORS.background,
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        top: 0,
        position: 'sticky',
        flexDirection: 'column',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 1400,
        padding: '16px 16px 0 16px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        {/* LEFT SIDE - Name and Phone */}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h5"
            sx={{
              fontSize: '1.4rem',
              fontWeight: 'bold',
              mb: 1,
              color: DASHBOARD_COLORS.header,
            }}
          >
            {userInfo.firstName} {userInfo.lastName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography
              variant="body1"
              sx={{ fontSize: '.9rem', color: COLORS.GREY }}
            >
              {userInfo.phoneNumber}
            </Typography>
            <ContentCopy
              onClick={handlePhoneCopyClick}
              sx={{
                fontSize: '.9rem',
                color: isPhoneCopied ? COLORS.SAFEGUARD_ORANGE : COLORS.GREY,
                cursor: 'pointer',
                transition: 'color 0.2s ease',
                '&:hover': { color: COLORS.SAFEGUARD_ORANGE },
              }}
            />
          </Box>
        </Box>

        {/* RIGHT SIDE - Status, Last seen, Location */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 0.5,
            minWidth: '180px',
            mr: 4,
          }}
        >
          {/* Status Row */}
          <Box
            sx={{ transform: 'scale(0.85)', transformOrigin: 'center right' }}
          >
            <UserStatusChip hasEmergency={false} userStatus={userStatus} />
          </Box>

          {/* Last Seen Row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {t(StringKeys.LAST_SEEN)}:
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: DASHBOARD_COLORS.mutedText }}
            >
              {lastUpdated ? formatTimeAgo(lastUpdated) : 'N/A'}
            </Typography>
          </Box>

          {/* Location Row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {locationToDisplay && (
              <ContentCopy
                onClick={handleLocationCopyClick}
                sx={{
                  fontSize: 16,
                  color: isLocationCopied
                    ? COLORS.SAFEGUARD_ORANGE
                    : COLORS.GREY,
                  cursor: 'pointer',
                  transition: 'color 0.2s ease',
                  '&:hover': { color: COLORS.SAFEGUARD_ORANGE },
                }}
              />
            )}
            <LocationOn
              sx={{ fontSize: 16, color: DASHBOARD_COLORS.success }}
            />
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {compassStatus === ConnectionStatus.Active
                ? t(StringKeys.LOCATION) + ':'
                : t(StringKeys.LAST_KNOWN_LOCATION) + ':'}
            </Typography>

            {/* Location is shown regardless of subscription tier — only tier-gated on the Map's markers. */}
            {locationToDisplay ? (
              <Typography
                component="button"
                onClick={handleCoordinatesClick}
                sx={{
                  color: DASHBOARD_COLORS.primary,
                  textDecoration: 'underline',
                  fontSize: '0.875rem',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  fontFamily: 'inherit',
                }}
              >
                {locationToDisplay.lat.toFixed(3)},{' '}
                {locationToDisplay.lng.toFixed(3)}
              </Typography>
            ) : (
              <Typography
                variant="body2"
                sx={{ color: DASHBOARD_COLORS.mutedText }}
              >
                N/A
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      {isSafeguardDev && (
        <Container
          sx={{ borderBottom: 1, borderColor: 'divider', width: '100%' }}
        >
          <Tabs
            value={tabValue}
            onChange={(e, val) => setTabValue(val)}
            variant="fullWidth"
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: DASHBOARD_COLORS.primary,
              },
              '&.Mui-selected': {
                color: DASHBOARD_COLORS.primary,
              },
            }}
          >
            <Tab label="User Info" value={0} />
            {/* <Tab label="Analytics" /> */}
            <Tab label="Developer" value={1} />
          </Tabs>
        </Container>
      )}
      {/* Close Button */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 5,
          right: 5,
          color: DASHBOARD_COLORS.header,
          zIndex: 1500,
        }}
      >
        <DisabledByDefaultRounded />
      </IconButton>
    </Box>
  );
};

export default UserDashboardHeader;
